import { create, type StoreApi, type UseBoundStore } from 'zustand';
import type { HYSACalculatorStore } from './types';
import {
  calculateCompoundInterest,
  isAmbiguousAnnualApyString,
  transformValuesToString,
} from './utils';
import { validate } from './validators';

export const defaultFormFieldValues: HYSACalculatorStore['formFields'] = {
  initialDeposit: '10000',
  monthlyDeposit: '250',
  annualPercentageYield: '4.35',
  timePeriod: '10',
};

type UpdateFieldOptions = {
  field: keyof HYSACalculatorStore['formFields'];
  value: string;
  formFields: HYSACalculatorStore['formFields'];
  set: (fn: (state: HYSACalculatorStore) => Partial<HYSACalculatorStore>) => void;
  additionalState?: Partial<HYSACalculatorStore>;
  finalize?: boolean;
};

function validateAndUpdateFieldSync({
  field,
  value,
  formFields,
  set,
  additionalState = {},
  finalize = false,
}: UpdateFieldOptions) {
  if (field === 'annualPercentageYield' && typeof value === 'string' && !finalize) {
    if (isAmbiguousAnnualApyString(value)) {
      set(state => ({
        formFields: {
          ...state.formFields,
          [field]: value,
        },
      }));
      return;
    }
  }

  const newFormFields = {
    ...formFields,
    [field]: value,
  };
  const result = validate(newFormFields);

  if (!result.success) {
    set(state => ({
      ...additionalState,
      formFields: {
        ...state.formFields,
        [field]: value,
      },
      errors: result.errors,
    }));
    return;
  }

  const results = calculateCompoundInterest(result.output);
  const validatedFormFields = transformValuesToString(result.output);
  set(() => ({
    ...additionalState,
    formFields: validatedFormFields,
    results,
    errors: {},
  }));
}

function runDeferredValidationAndResults(
  get: () => HYSACalculatorStore,
  set: (fn: (state: HYSACalculatorStore) => Partial<HYSACalculatorStore>) => void,
) {
  const { formFields } = get();

  const result = validate(formFields);

  if (!result.success) {
    set(() => ({
      errors: result.errors,
    }));
    return;
  }

  const nextResults = calculateCompoundInterest(result.output);
  const validatedFormFields = transformValuesToString(result.output);
  set(() => ({
    formFields: {
      ...validatedFormFields,
      annualPercentageYield: formFields.annualPercentageYield.toString(),
    },
    results: nextResults,
    errors: {},
  }));
}

let useHYSACalculatorStore: UseBoundStore<StoreApi<HYSACalculatorStore>>;

export function createHYSACalculatorStore(defaultFormValues?: HYSACalculatorStore['formFields']) {
  let defaultValues = defaultFormFieldValues;

  if (defaultFormValues) {
    defaultValues = {
      ...defaultValues,
      ...defaultFormValues,
    };
  }

  const formFieldsAsNumbers = {
    initialDeposit: Number(defaultValues.initialDeposit),
    monthlyDeposit: Number(defaultValues.monthlyDeposit),
    annualPercentageYield: Number(defaultValues.annualPercentageYield),
    timePeriod: Number(defaultValues.timePeriod),
  };

  if (Object.values(formFieldsAsNumbers).some(val => isNaN(val))) {
    throw new Error('One of the default HYSA form fields is NaN');
  }

  const defaultResults = calculateCompoundInterest(formFieldsAsNumbers);

  const store = create<HYSACalculatorStore>((set, get) => {
    let deferredRafId: number | null = null;

    function scheduleDeferredValidation() {
      if (deferredRafId !== null) {
        return;
      }
      deferredRafId = requestAnimationFrame(() => {
        deferredRafId = null;
        runDeferredValidationAndResults(get, set);
      });
    }

    return {
      isTimePeriodOpen: false,
      formFields: transformValuesToString(formFieldsAsNumbers),
      results: defaultResults,
      errors: {},
      handleTimePeriodOpen: (field, value, isOpened) => {
        set(state => ({
          formFields: {
            ...state.formFields,
            [field]: value,
          },
          isTimePeriodOpen: isOpened,
        }));
        scheduleDeferredValidation();
      },
      setField: (field, value, options) => {
        if (options?.finalize) {
          const { formFields } = get();
          validateAndUpdateFieldSync({
            field,
            value,
            formFields,
            set,
            finalize: true,
          });
          return;
        }

        set(state => ({
          formFields: {
            ...state.formFields,
            [field]: value,
          },
        }));
        scheduleDeferredValidation();
      },
    };
  });
  useHYSACalculatorStore = store;
  return store;
}

export { useHYSACalculatorStore };
